import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { SortingDirection } from 'common/types/SortingOptions';

import {
  PricesRepository,
  SearchCustomerServiceTypesParams,
} from 'domain/repositories/IPricesRepository';
import { Entity } from 'domain/types/Entity';
import { ServiceType } from 'domain/entities/ServiceType';
import { useHookInjection } from 'application/hooks';
import { useSortingAndPagination } from 'application/common';

const QUERY_PREFIX = 'CUSTOMER_SERVICE_TYPES';

export const useCaseSearchCustomerServiceTypes = (
  params: Omit<SearchCustomerServiceTypesParams, 'offset'>,
) => {
  const qClient = useQueryClient();
  const { searchCustomerServiceTypes } = useHookInjection<PricesRepository>(PricesRepository);

  const { paging, sorting, ...utils } = useSortingAndPagination<Entity<ServiceType>>(
    'customerName',
    SortingDirection.Ascending,
    params.limit ?? 200,
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [QUERY_PREFIX, { ...params, ...paging, ...sorting }] as const,
    queryFn: ({ queryKey }) => searchCustomerServiceTypes(queryKey[1]),
    placeholderData: { items: [], total: 0 },
    enabled: Boolean(!!params && params.customerId),
    staleTime: 10_000,
  });

  const refetch = useCallback(
    () => qClient.invalidateQueries({ queryKey: [QUERY_PREFIX] }),
    [qClient],
  );

  return {
    ...data,
    refetch,

    paging,
    sorting,
    ...utils,

    isLoading,
    isFetching,
    error,
  };
};
